import { createSelector } from 'reselect';
import {
    compose,
    concat,
    filter,
    forEach,
    merge,
    flatten,
    omit,
    map,
    prop,
    values,
    update,
    set,
    reduce,
    get,
    valuesIn,
    includes,
    isEmpty,
    intersection,
    pick,
    flatMap,
    last,
    isEqual,
    groupBy,
    has,
    __,
} from 'lodash/fp';
import { getBooleanProp, ifElse, keyById } from '@mews/fp';
import { ResourceBlockType } from '@mews-commander/data-access';
import { createAsyncMetaSelectors } from '@mews/framework';
import { mapId, isStringArraySubsetOfStringArray } from '@mews-commander/utils';
import { hasPrivilege } from '@mews-commander/core';
import { ApplicationState } from '@mews/business-components/components/resDiary/types';
import { getCurrentEnterpriseIanaTimeZoneIdentifier } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import {
    getResourcesWithoutCategory,
    getServiceResources,
    getUniqueServiceResourceFloorNumbers,
} from '@mews-commander/legacy-entities/src/selectors/spaces';
import { constructResourcesTree } from '@mews-commander/legacy-entities/src/ducks/resourcesTree';
import { getSpaces } from '@mews-commander/legacy-entities/src/ducks/spaces';
import { getResourceCategoryIds } from '@mews-commander/legacy-entities/src/ducks/resourceCategories';
import { getResourceFeatureAssignments } from '@mews-commander/legacy-entities/src/ducks/resourceFeatureAssignments';
import {
    META_STATE_STARTED,
    META_STATE_SUCCEEDED,
    META_STATE_FAILED,
} from '@mews-commander/legacy-entities/src/constants';
import { normalizeClusters } from '../../resourceUseCluster';
import getRangesHash from '../utils/getRangesHash';
import {
    RESERVATION_FETCH_STARTED,
    RESERVATION_FETCH_SUCCEEDED,
    RESERVATION_FETCH_FAILED,
    TIMELINE_RESOURCE_USES_FETCH_STARTED,
    TIMELINE_RESOURCE_USES_FETCH_SUCCEEDED,
    TIMELINE_RESOURCE_USES_FETCH_FAILED,
    RESOURCE_USES_MODIFIED,
    RESOURCE_ASSIGNED_STARTED,
    RESOURCE_ASSIGNED_SUCCEEDED,
    RESOURCE_ASSIGNED_FAILED,
    UPDATE_ASSIGNED_RESOURCE_FIXED_FETCH_STARTED,
    UPDATE_ASSIGNED_RESOURCE_FIXED_FETCH_SUCCEEDED,
    UPDATE_ASSIGNED_RESOURCE_FIXED_FETCH_FAILED,
    UPDATE_TIMELINE_RESOURCE_USES_FETCH_STARTED,
    UPDATE_TIMELINE_RESOURCE_USES_FETCH_SUCCEEDED,
    UPDATE_TIMELINE_RESOURCE_USES_FETCH_FAILED,
    RANGE_CHANGED,
    RESET,
    CUSTOMERS_CHANGED,
    RESOURCE_USE_DESELECTED,
    RESOURCE_USE_SELECTED,
    RANGES_FETCH_STARTED,
    RANGES_FETCH_SUCCEEDED,
    RANGES_FETCH_FAILED,
    OPTIMIZE_ASSIGNMENT_STARTED,
    OPTIMIZE_ASSIGNMENT_SUCCEEDED,
    OPTIMIZE_ASSIGNMENT_FAILED,
    CANCEL_OPTIMIZATION,
    VIEWPORT_CHANGED,
    PREVIEW_DIALOG_TOGGLED,
    PREVIEW_REQUEST_STARTED,
    MONTH_VIEW_RESERVATION_LENGTH_RESTRICTION_INFO_MESSAGE_DISPLAYED,
    TIMELINE_INIT_STARTED,
    TIMELINE_INIT_SUCCEEDED,
    TIMELINE_INIT_FAILED,
    OPEN_AVAILABILITY_WARNING_DIALOG,
    CLOSE_AVAILABILITY_WARNING_DIALOG,
    BLOCKS_FETCH_STARTED,
    BLOCKS_FETCH_SUCCEEDED,
    BLOCKS_FETCH_FAILED,
    BLOCK_CHANGED,
} from '../actions/types';
import moduleSelector from '../selector';
import { TimelineService } from '../timelineService';
import { getSelectedTimeUnit } from '../selectors/view';
import { AvailabilityWarningProps } from '../types';
import { TimelineFilterSchemaKeys, TimelineFilterValues } from '../components/TimelineFilters/timelineFilterSchema';

export const NAME = 'timeline';

const initialState = {
    apiCallsRunning: 0,
    currentViewportRange: {},
    customers: {},
    loadedBlocks: {},
    loadedRanges: {},
    loadingRanges: {},
    loadingBlocks: {},
    isAssigningResource: false,
    isMonthViewReservationLengthRestrictionInfoMessageDisplayed: false,
    isPreviewDialogOpened: false,
    optimisationInProgress: false,
    prevTimelineResourceUses: {},
    resourceMovementsCount: 0,
    timelineResourceUses: {},
    selectedResourceUse: null,
    filter: {},
    ...META_STATE_STARTED,
};

export default function timelineReducer(state = initialState, action: $TSFixMe) {
    switch (action.type) {
        case RESET:
            return {
                ...initialState,
                meta: state.meta,
                isMonthViewReservationLengthRestrictionInfoMessageDisplayed:
                    state.isMonthViewReservationLengthRestrictionInfoMessageDisplayed,
            };
        case RESOURCE_USE_DESELECTED: {
            return {
                ...state,
                selectedResourceUse: null,
            };
        }
        case RESOURCE_USE_SELECTED: {
            return {
                ...state,
                selectedResourceUse: action.payload,
            };
        }
        case TIMELINE_RESOURCE_USES_FETCH_STARTED:
        case UPDATE_TIMELINE_RESOURCE_USES_FETCH_STARTED:
        case RESERVATION_FETCH_STARTED:
            return {
                ...state,
                apiCallsRunning: state.apiCallsRunning + 1,
            };
        case RESOURCE_ASSIGNED_STARTED:
        case UPDATE_ASSIGNED_RESOURCE_FIXED_FETCH_STARTED:
            return {
                ...state,
                isAssigningResource: true,
                apiCallsRunning: state.apiCallsRunning + 1,
            };
        case UPDATE_TIMELINE_RESOURCE_USES_FETCH_FAILED:
        case TIMELINE_RESOURCE_USES_FETCH_FAILED:
        case RESERVATION_FETCH_FAILED:
            return {
                ...state,
                apiCallsRunning: state.apiCallsRunning - 1,
            };
        case RESOURCE_ASSIGNED_FAILED:
        case UPDATE_ASSIGNED_RESOURCE_FIXED_FETCH_FAILED:
            return {
                ...state,
                isAssigningResource: false,
                apiCallsRunning: state.apiCallsRunning - 1,
            };
        case RESERVATION_FETCH_SUCCEEDED:
            return {
                ...state,
                timelineResourceUses: action.payload,
                apiCallsRunning: state.apiCallsRunning - 1,
            };
        case RESOURCE_USES_MODIFIED:
            return {
                ...state,
                timelineResourceUses: action.payload,
            };
        case TIMELINE_RESOURCE_USES_FETCH_SUCCEEDED:
        case UPDATE_TIMELINE_RESOURCE_USES_FETCH_SUCCEEDED: {
            return {
                ...state,
                timelineResourceUses: action.payload,
                apiCallsRunning: state.apiCallsRunning - 1,
            };
        }
        case RESOURCE_ASSIGNED_SUCCEEDED:
            return {
                ...state,
                timelineResourceUses: action.payload,
                isAssigningResource: false,
                apiCallsRunning: state.apiCallsRunning - 1,
            };
        case UPDATE_ASSIGNED_RESOURCE_FIXED_FETCH_SUCCEEDED:
            return {
                ...state,
                timelineResourceUses: action.payload,
                isAssigningResource: false,
                apiCallsRunning: state.apiCallsRunning - 1,
            };
        case RANGE_CHANGED:
            return {
                ...state,
                loadedRanges: action.payload.range,
            };
        case BLOCK_CHANGED:
            return {
                ...state,
                loadedBlocks: action.payload.block,
            };
        case CUSTOMERS_CHANGED:
            return {
                ...state,
                customers: {
                    ...state.customers,
                    ...action.payload,
                },
            };
        case RANGES_FETCH_STARTED:
            return reduce(
                (acc, range) => set(['loadingRanges', getRangesHash(range)], range, acc),
                state,
                action.payload
            );
        case RANGES_FETCH_SUCCEEDED:
        case RANGES_FETCH_FAILED:
            // @ts-expect-error $TSFixMeError: No overload matches this call.
            return update(['loadingRanges'], (ranges) => omit([map(getRangesHash)(action.payload)], ranges), state);
        case BLOCKS_FETCH_STARTED:
            return reduce(
                (acc, block) => set(['loadingBlocks', getRangesHash(block)], block, acc),
                state,
                action.payload
            );
        case BLOCKS_FETCH_SUCCEEDED:
        case BLOCKS_FETCH_FAILED:
            // @ts-expect-error $TSFixMeError: No overload matches this call.
            return update(['loadingBlocks'], (blocks) => omit([map(getRangesHash)(action.payload)], blocks), state);
        case OPTIMIZE_ASSIGNMENT_STARTED:
            return {
                ...state,
                optimisationInProgress: true,
            };
        case OPTIMIZE_ASSIGNMENT_SUCCEEDED:
            return {
                ...state,
                prevTimelineResourceUses: { ...state.timelineResourceUses },
                timelineResourceUses: {
                    ...action.payload.assignments,
                    // @ts-expect-error in undefined key are blockAssignments since they don't have requestedResourceCategory
                    undefined: state.timelineResourceUses.undefined ?? [],
                },
                resourceMovementsCount: action.payload.resourceMovementsCount,
                optimisationInProgress: false,
            };
        case OPTIMIZE_ASSIGNMENT_FAILED:
            return {
                ...state,
                optimisationInProgress: false,
            };
        case TIMELINE_INIT_STARTED:
            return {
                ...state,
                ...META_STATE_STARTED,
            };
        case TIMELINE_INIT_SUCCEEDED:
            return {
                ...state,
                ...META_STATE_SUCCEEDED,
            };
        case TIMELINE_INIT_FAILED:
            return {
                ...state,
                ...META_STATE_FAILED,
            };
        case VIEWPORT_CHANGED:
            return {
                ...state,
                ...META_STATE_SUCCEEDED,
                currentViewportRange: action.payload,
            };
        case PREVIEW_DIALOG_TOGGLED:
            return {
                ...state,
                isPreviewDialogOpened: action.payload,
            };
        case CANCEL_OPTIMIZATION:
            return {
                ...state,
                timelineResourceUses: { ...state.prevTimelineResourceUses },
            };
        case PREVIEW_REQUEST_STARTED:
            return {
                ...state,
                previewedOptimizeRangeUTC: action.payload,
            };
        case MONTH_VIEW_RESERVATION_LENGTH_RESTRICTION_INFO_MESSAGE_DISPLAYED:
            return {
                ...state,
                isMonthViewReservationLengthRestrictionInfoMessageDisplayed: action.payload,
            };
        case OPEN_AVAILABILITY_WARNING_DIALOG:
            return { ...state, availabilityWarningDialogProps: action.payload };
        case CLOSE_AVAILABILITY_WARNING_DIALOG:
            return { ...state, availabilityWarningDialogProps: undefined };
        default:
            return state;
    }
}

export const getTimeline = createSelector(moduleSelector, (state) => state[NAME]);

const getTimelineResourceUses = createSelector(getTimeline, (state) => state.timelineResourceUses);
// @ts-expect-error $TSFixMeError: No overload matches this call.
export const getFlatTimelineResourceUses = createSelector(getTimelineResourceUses, compose(keyById, flatten, valuesIn));

export const getCustomers = createSelector(getTimeline, (state) => state.customers);

export const isLoading = createSelector(getTimeline, (state) => state.apiCallsRunning > 0);

const getModuleMeta = createSelector(getTimeline, (state) => state.meta);

export const [isFetchingTimeline] = createAsyncMetaSelectors(getModuleMeta);

export const isAssigningResource = createSelector(getTimeline, (state) => state.isAssigningResource);

export const getServiceId = createSelector(getTimeline, (state) => state.serviceId);

export const getRanges = createSelector(getTimeline, (state) => state.loadedRanges);

export const getLoadedBlocks = createSelector(getTimeline, (state) => values(state.loadedBlocks));

export const getLoadingRanges = createSelector(getTimeline, (state) => values(state.loadingRanges));
export const getLoadingBlocks = createSelector(getTimeline, (state) => values(state.loadingBlocks));

export const isSidebarOpened = createSelector(getTimeline, (state) => !!state.selectedResourceUse);

export const getSelectedResourceUse = createSelector(getTimeline, (state) => state.selectedResourceUse);

export const isOptimisationInProgress = createSelector(getTimeline, get('optimisationInProgress'));

export const getResourceMovements = createSelector(getTimeline, get('resourceMovementsCount'));

export const getTimelineAvailabilityDialogProps = createSelector(
    getTimeline,
    (state): AvailabilityWarningProps | undefined => state.availabilityWarningDialogProps
);

export const getResources = createSelector(
    getServiceResources,
    getUniqueServiceResourceFloorNumbers,
    getResourceCategoryIds,
    getResourceFeatureAssignments,
    (state: ApplicationState, props: { filterValues: TimelineFilterValues }) => props.filterValues,
    (resources, allFloorNumbers, allResourceCategoryIds, resourceFeatureAssignments, timelineFilters) => {
        const {
            [TimelineFilterSchemaKeys.FloorNumber]: selectedFloorNumbers,
            [TimelineFilterSchemaKeys.ResourceFeatureId]: selectedResourceFeatureIds,
            [TimelineFilterSchemaKeys.ResourceCategoryId]: selectedResourceCategoryIds,
        } = timelineFilters;

        // logic of timeline filters: when nothing is selected then everything is selected

        const areAllFloorNumbersSelected =
            isEqual(allFloorNumbers, selectedFloorNumbers) || isEmpty(selectedFloorNumbers);

        const areAllResourceCategoryIdsSelected =
            isEqual(allResourceCategoryIds, selectedResourceCategoryIds) || isEmpty(selectedResourceCategoryIds);

        const areSelectedResourceFeatureIdsEmpty = isEmpty(selectedResourceFeatureIds);

        if (areAllFloorNumbersSelected && areAllResourceCategoryIdsSelected && areSelectedResourceFeatureIdsEmpty) {
            return resources;
        }

        if (isEmpty(selectedFloorNumbers) || isEmpty(selectedResourceCategoryIds)) {
            return [];
        }

        let filteredResources = resources;

        if (!areAllFloorNumbersSelected) {
            const hasFloor = includes(__, selectedFloorNumbers);
            // @ts-expect-error $TSFixMeError: Property 'data' does not exist on type 'number | (() => IterableIterator<never>) | (() => { copyWithin: boolean; entries...
            filteredResources = filter(({ data }) => hasFloor(data?.value?.floorNumber), filteredResources);
        }

        if (!areAllResourceCategoryIdsSelected) {
            const groupedResources = groupBy('resourceCategoryId')(filteredResources);
            filteredResources = compose(flatten, values, pick(selectedResourceCategoryIds ?? []))(groupedResources);
        }

        if (!areSelectedResourceFeatureIdsEmpty) {
            const groupedResourceFeatureAssignments = groupBy('resourceId')(resourceFeatureAssignments);
            // @ts-expect-error $TSFixMeError: Type '{ id: any; }[]' is not assignable to type '(number | (() => IterableIterator<never>) | (() => { copyWithin: boolea...
            filteredResources = filter(({ id }) =>
                has(id, groupedResourceFeatureAssignments)
                    ? isStringArraySubsetOfStringArray(
                          selectedResourceFeatureIds ?? [],
                          map('featureId', groupedResourceFeatureAssignments[id])
                      )
                    : false
            )(filteredResources);
        }

        return filteredResources;
    }
);

export const getResourcesIncludingNonCategory = createSelector(getResources, getResourcesWithoutCategory, concat);

// @ts-expect-error $TSFixMeError: No overload matches this call.
export const getSortedResources = createSelector(getResourcesIncludingNonCategory, constructResourcesTree);

export const getCurrentViewportRange = createSelector(getTimeline, get('currentViewportRange'));

export const isPreviewDialogOpened = createSelector(getTimeline, get('isPreviewDialogOpened'));

export const hasEditButton = createSelector(
    getSelectedResourceUse,
    () => hasPrivilege('manageSpaces'),
    (resourceUse, canManageResources) => {
        // @ts-expect-error $TSFixMeError: Element implicitly has an 'any' type because expression of type 'any' can't be used to index type 'typeof ResourceBlockT...
        const resourceBlockType = ResourceBlockType[prop('type', resourceUse)];
        if (prop('isReservation', resourceUse)) {
            return false;
        }
        if (resourceBlockType === ResourceBlockType.InternalUse) {
            return true;
        } else if (resourceBlockType === ResourceBlockType.OutOfOrder && canManageResources) {
            return true;
        }
        return false;
    }
);

export const getPreviewedOptimizeRangeUTC = createSelector(getTimeline, get('previewedOptimizeRangeUTC'));

const getForeignResourceUses = createSelector(getFlatTimelineResourceUses, getResources, (uses, serviceResources) => {
    const serviceResourceIds = mapId(serviceResources);
    return filter(({ assignedResourceId }) => !includes(assignedResourceId, serviceResourceIds))(uses);
});

// @ts-expect-error $TSFixMeError: 'getAllParents' implicitly has return type 'any' because it does not have a return type annotation and is referenced dir...
const getAllParents = (resource: $TSFixMe, resources: $TSFixMe) =>
    resource.parentId ? [...getAllParents(resources[resource.parentId], resources), resource.parentId] : [];

const getAllChildren = (resource: $TSFixMe, resources: $TSFixMe): unknown[] => {
    const children = filter({ parentId: resource.id }, resources);
    return isEmpty(children)
        ? []
        : [...mapId(children), ...flatMap((child) => getAllChildren(child, resources))(children)];
};

export const computeResourceRelatives = (keyedResources: $TSFixMe) =>
    reduce((relativesMap, resource) => {
        // @ts-expect-error $TSFixMeError: Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{}'.
        relativesMap[(resource as $TSFixMe).id] = {
            parents: getAllParents(resource, keyedResources),
            children: getAllChildren(resource, keyedResources),
        };
        return relativesMap;
    }, {})(keyedResources);

const getResourceRelativesMap = createSelector(getSpaces, compose(computeResourceRelatives, keyById));

const getResourceRelatedIds = (targetResource: $TSFixMe, resourcesMap: $TSFixMe) => get(targetResource)(resourcesMap);

export const getTimelineTimeUtcToUnitFunction = createSelector(
    getCurrentEnterpriseIanaTimeZoneIdentifier,
    getSelectedTimeUnit,
    (timezone: string, timeUnit) => (utcString: string) =>
        TimelineService.utcToTimeUnitIndex(utcString, timezone, timeUnit)
);

// @ts-expect-error $TSFixMeError: Parameter 'use' implicitly has an 'any' type.
const createCluster = (use, relatedResourceId) => ({
    ...pick(
        [
            'startUtc',
            'endUtc',
            'maxX',
            'minX',
            'name',
            'requestedCategoryId',
            'isReservation',
            'serviceName',
            'displaceY',
            'serviceId',
            'customerId',
        ],
        use
    ),

    isMovable: false,
    id: `cluster-${use.id}-${use.assignedResourceId}-${relatedResourceId}`,
    assignedResourceId: relatedResourceId,
    serviceIds: [use.serviceId],
    serviceNames: [use.serviceName],
    resourceUseIds: [use.id],
    isMultipleServices: false,
});

// @ts-expect-error $TSFixMeError: 'getDeepestAvailableChildren' implicitly has return type 'any' because it does not have a return type annotation and is ...
const getDeepestAvailableChildren = (
    resourceChildrenIds: $TSFixMe,
    serviceResourceIds: $TSFixMe,
    resourceRelativesMap: $TSFixMe
) =>
    // @ts-expect-error $TSFixMeError: No overload matches this call.
    reduce((acc, resourceId) => {
        // @ts-expect-error $TSFixMeError: Type 'unknown' cannot be used as an index type.
        const { children } = resourceRelativesMap[resourceId];
        const subChildren = intersection(children, serviceResourceIds);
        if (!subChildren.length) {
            // @ts-expect-error $TSFixMeError: Argument of type 'unknown' is not assignable to parameter of type 'never'.
            acc.push(resourceId);
            return acc;
        }
        return concat(acc, getDeepestAvailableChildren(subChildren, serviceResourceIds, resourceRelativesMap));
    }, [])(resourceChildrenIds);

export const generateClusters = (resourceIds: $TSFixMe, uses: $TSFixMe, resourceRelativesMap: $TSFixMe) =>
    reduce(
        (acc, use) => {
            if (!includes(use.assignedResourceId, resourceIds)) {
                const resourceRelativeIds = getResourceRelatedIds(use.assignedResourceId, resourceRelativesMap);
                if (resourceRelativeIds) {
                    const resourceParentIds = intersection(resourceIds, (resourceRelativeIds as $TSFixMe).parents);
                    const resourceChildrenIds = intersection(resourceIds, (resourceRelativeIds as $TSFixMe).children);
                    if (resourceParentIds.length) {
                        // @ts-expect-error $TSFixMeError: Argument of type '{ isMovable: boolean; id: string; assignedResourceId: any; serviceIds: any[]; serviceNames: any[]; res...
                        acc.push(createCluster(use, last(resourceParentIds)));
                    }
                    if (resourceChildrenIds.length) {
                        const deepestChildren = getDeepestAvailableChildren(
                            resourceChildrenIds,
                            resourceIds,
                            resourceRelativesMap
                        );
                        forEach((resourceId) => {
                            // @ts-expect-error $TSFixMeError: Argument of type '{ isMovable: boolean; id: string; assignedResourceId: any; serviceIds: any[]; serviceNames: any[]; res...
                            acc.push(createCluster(use, resourceId));
                        })(deepestChildren);
                    }
                }
            }
            return acc;
        },
        [],
        uses
    );

const getReservationClusters = createSelector(
    getForeignResourceUses,
    getResources,
    getResourceRelativesMap,
    getTimelineTimeUtcToUnitFunction,
    (uses, serviceResources, resourceRelativesMap, utcToUnit) => {
        const resourceIds = mapId(serviceResources);
        const clusters = generateClusters(resourceIds, uses, resourceRelativesMap);
        return normalizeClusters(clusters, utcToUnit);
    }
);

const getTimelineResourceUsesAndClusters = createSelector(getFlatTimelineResourceUses, getReservationClusters, merge);

export const getTimelineResourceUsesAndClustersOptimized = ifElse(
    getBooleanProp('hasMultipleServices'),
    // @ts-expect-error
    getTimelineResourceUsesAndClusters,
    getFlatTimelineResourceUses
);
